/**
 * SHELL DE MARCA de la ficha visual del proyecto.
 *
 * El campo `showcase.body` de la API guarda **solo el cuerpo**; este archivo pone el `<head>` y se
 * los concatena al pintar. Por qué así y no un HTML autocontenido por proyecto: son decenas de
 * fichas, y con el estilo dentro de cada una un cambio de identidad visual obligaría a regenerarlas
 * de a una. Aquí la marca vive en un solo sitio y las alcanza a todas de golpe.
 *
 * Y es lo que mantiene BAJA la carga cognitiva: la forma es siempre la misma —las nueve secciones
 * de abajo, en ese orden— y lo único que cambia entre un proyecto y otro es el contenido. Quien
 * abre la tercera ficha ya sabe dónde mirar.
 *
 * Los tokens salen de `Software_Docs/_vault/99-Meta/rendafin-brand-tokens.md` y el lenguaje visual
 * de `sprint-report-template.html`, que es la plantilla del reporte de TI.
 *
 * ⚠️ Se inyecta como `srcdoc` de un iframe con `sandbox` — ver `RoadmapProjectShowcaseComponent`.
 */
export const SHOWCASE_SECTIONS = [
  'hero',
  'problema',
  'cambio',
  'kpis',
  'incluye',
  'por-rol',
  'no-incluye',
  'costo',
  'cuando',
] as const

export const SHOWCASE_SHELL = `<!doctype html>
<html lang="es-MX">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800&display=swap" rel="stylesheet">
<style>
  :root{
    --primary:#202835; --green:#22C55E; --text:#495057; --soft:#6c757d;
    --bg:#eef2f7; --surface:#ffffff; --border:#e3e9f0;
    --api:#3B82F6; --plat:#8B5CF6; --canal:#EC4899;
    --amber:#F59E0B; --red:#EF4444;
  }
  *{box-sizing:border-box; margin:0; padding:0;}
  body{
    font-family:'Montserrat',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;
    background:var(--bg); color:var(--text); font-size:14px; line-height:1.6;
    padding:0 0 56px;
  }
  .ficha{max-width:1060px; margin:0 auto; padding:0 26px;}

  /* ══ Tipografía base ══ */
  h2{font-size:19px; font-weight:800; color:var(--primary); letter-spacing:-.2px;}
  h3{font-size:14px; font-weight:700; color:var(--primary);}
  p{margin-bottom:10px;}
  p:last-child{margin-bottom:0;}
  strong,b{font-weight:700; color:var(--primary);}
  .accent{color:var(--green);}
  .nota{font-size:12.5px; color:var(--soft); font-style:italic;}
  a{color:var(--api); text-decoration:none; border-bottom:1px solid rgba(59,130,246,.35);}

  /* ══ 1 · Portada ══ */
  .hero{
    background:
      repeating-linear-gradient(45deg, rgba(34,197,94,.06) 0 1px, transparent 1px 46px),
      repeating-linear-gradient(-45deg, rgba(59,130,246,.06) 0 1px, transparent 1px 46px),
      linear-gradient(120deg,#202835 0%, #283447 100%);
    color:#fff; padding:34px 0 38px; margin-bottom:32px;
  }
  /* Todos los hijos comparten LA MISMA caja que .ficha, o el título, la marca y el lead arrancan
     cada uno en una x distinta. Por eso ninguno lleva ancho propio: la medida la da el contenedor. */
  .hero > *{display:block; max-width:1060px; margin-left:auto; margin-right:auto; padding:0 26px;}
  .hero .brand{position:relative; font-weight:800; font-size:20px;
    letter-spacing:.5px; line-height:1; padding-bottom:10px; margin-bottom:20px;}
  .hero .brand::after{content:''; position:absolute; left:26px; bottom:0; width:150px; height:2px; background:rgba(255,255,255,.45);}
  .hero .brand::before{content:''; position:absolute; left:26px; bottom:-1px; width:62px; height:4px; background:var(--green); z-index:1;}
  .hero h1{font-size:33px; font-weight:800; line-height:1.1; color:#fff; letter-spacing:-.5px;}
  .hero h1 .accent{color:var(--green);}
  .hero .lead{margin-top:12px; font-size:15px; font-weight:300; color:#c3cdd9; line-height:1.55;}
  .hero .meta{margin-top:20px; display:flex; flex-wrap:wrap; gap:9px; padding-left:26px; padding-right:26px;}
  .hero .meta span{font-size:10.5px; font-weight:600; letter-spacing:.14em; text-transform:uppercase;
    color:#9fb0c4; border:1px solid rgba(255,255,255,.18); border-radius:999px; padding:6px 13px;}

  /* ══ Sección genérica ══ */
  section{margin-bottom:30px;}
  section > h2{margin-bottom:14px; display:flex; align-items:center; gap:10px;}
  section > h2::before{content:''; width:4px; height:17px; border-radius:2px; background:var(--green); flex:none;}

  /* ══ 2 · El problema hoy ══ */
  .problema ul{list-style:none;}
  .problema li{background:var(--surface); border:1px solid var(--border); border-left:3px solid var(--amber);
    border-radius:10px; padding:13px 17px; margin-bottom:9px;}
  .problema li:last-child{margin-bottom:0;}

  /* ══ 3 · Qué cambia (antes / después) ══ */
  .cambio .par{display:grid; grid-template-columns:1fr 1fr; gap:14px;}
  .cambio .lado{background:var(--surface); border:1px solid var(--border); border-radius:13px; padding:18px 20px;}
  .cambio .antes{border-top:3px solid var(--soft);}
  .cambio .despues{border-top:3px solid var(--green);}
  .cambio .lado h3{margin-bottom:11px; font-size:11px; letter-spacing:.1em; text-transform:uppercase; color:var(--soft);}
  .cambio .despues h3{color:var(--green);}
  .cambio ul{list-style:none;}
  .cambio li{padding-left:19px; position:relative; margin-bottom:8px;}
  .cambio li:last-child{margin-bottom:0;}
  .cambio .antes li::before{content:'–'; position:absolute; left:0; color:var(--soft); font-weight:700;}
  .cambio .despues li::before{content:'✓'; position:absolute; left:0; color:var(--green); font-weight:700;}

  /* ══ 4 · Lo que suma, en números ══ */
  .kpis .tiles{display:grid; grid-template-columns:repeat(auto-fit,minmax(150px,1fr)); gap:12px;}
  .kpis .tile{background:var(--surface); border:1px solid var(--border); border-radius:13px;
    padding:18px 16px; text-align:center; box-shadow:0 1px 6px rgba(32,40,53,.04);}
  .kpis .n{font-size:30px; font-weight:800; color:var(--primary); line-height:1;}
  .kpis .n.g{color:var(--green);}
  .kpis .l{margin-top:8px; font-size:10px; font-weight:600; color:var(--soft);
    letter-spacing:.05em; text-transform:uppercase; line-height:1.35;}

  /* ══ 5 · Qué incluye ══ */
  .incluye .cards{display:grid; grid-template-columns:repeat(auto-fit,minmax(280px,1fr)); gap:12px;}
  .incluye .card{background:var(--surface); border:1px solid var(--border); border-radius:12px;
    padding:15px 17px; border-top:3px solid var(--api);}
  .incluye .card h3{margin-bottom:6px;}
  .incluye .card p{font-size:13px;}

  /* ══ 6 · Cómo se ve por rol ══ */
  .por-rol .fila{display:grid; grid-template-columns:170px 1fr; gap:16px; align-items:start;
    background:var(--surface); border:1px solid var(--border); border-radius:11px;
    padding:14px 18px; margin-bottom:9px;}
  .por-rol .fila:last-child{margin-bottom:0;}
  .por-rol .quien{font-weight:700; color:var(--primary); font-size:13px;}

  /* ══ 7 · Qué NO incluye ══ */
  .no-incluye{background:#f7fafd; border:1px dashed var(--border); border-radius:13px; padding:20px 24px;}
  .no-incluye ul{list-style:none;}
  .no-incluye li{padding-left:19px; position:relative; margin-bottom:7px; color:var(--soft);}
  .no-incluye li:last-child{margin-bottom:0;}
  .no-incluye li::before{content:'×'; position:absolute; left:0; color:var(--red); font-weight:700;}

  /* ══ 8 · Qué costaría por fuera ══ */
  .costo{background:var(--surface); border:1px solid var(--border); border-left:4px solid var(--green);
    border-radius:13px; padding:22px 26px;}
  .costo .cifra{font-size:32px; font-weight:800; color:var(--primary); line-height:1.1;}
  .costo .cifra small{font-size:13px; font-weight:600; color:var(--soft); margin-left:8px;}
  .costo .detalle{margin-top:12px;}

  /* ══ 9 · Cuándo ══ */
  .cuando ol{list-style:none; border-left:2px solid var(--border); margin-left:7px; padding-left:22px;}
  .cuando li{position:relative; margin-bottom:15px;}
  .cuando li:last-child{margin-bottom:0;}
  .cuando li::before{content:''; position:absolute; left:-29px; top:6px; width:11px; height:11px;
    border-radius:50%; background:var(--green); box-shadow:0 0 0 3px var(--bg);}
  .cuando .hito{font-weight:700; color:var(--primary);}
  .cuando .fecha{font-size:11px; font-weight:600; color:var(--soft); letter-spacing:.05em; text-transform:uppercase;}

  /* ══ Tabla, para catálogos ══ */
  table{width:100%; border-collapse:collapse; background:var(--surface);
    border:1px solid var(--border); border-radius:12px; overflow:hidden; font-size:13px;}
  th{background:#f7fafd; text-align:left; padding:11px 15px; font-size:10.5px; font-weight:700;
    color:var(--soft); letter-spacing:.06em; text-transform:uppercase; border-bottom:1px solid var(--border);}
  td{padding:11px 15px; border-bottom:1px solid var(--border); vertical-align:top;}
  tr:last-child td{border-bottom:none;}

  @media print{ body{background:#fff;} .hero{-webkit-print-color-adjust:exact; print-color-adjust:exact;} }
</style>
</head>
<body>
`

/** Arma el documento completo. Es lo único que sabe cómo se pega el shell con el cuerpo. */
export function buildShowcaseDoc(body: string): string {
  return `${SHOWCASE_SHELL}${body}\n</body>\n</html>`
}
